import React, { useEffect, useState } from "react";
import { DEFAULT_PRODUCTS } from "@/lib/products";
import { useNavigate } from "react-router-dom";
import api, { formatError, downloadFile } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Eye, Filter, CalendarDays, FileSpreadsheet } from "lucide-react";
import { fmtINR, fmtNum, fmtDate } from "@/utils/format";
import { toast } from "sonner";

const isoDaysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function AdminDailyReports() {
  const nav = useNavigate();
  const [range, setRange] = useState({ from_date: isoDaysAgo(29), to_date: isoDaysAgo(0) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/daily-reports", { params: range });
      setData(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pdf = (d) => downloadFile(`/export/daily/${d}.pdf`, `daily_report_${d}.pdf`).catch((e) => toast.error(e.message));
  const t = data?.totals;
  const products = data?.products || DEFAULT_PRODUCTS;

  return (
    <AdminLayout>
      <h1 className="text-3xl font-display font-bold mb-1">Daily Reports</h1>
      <p className="text-sm text-slate-500 mb-6">All 3 shifts combined per business day · download A4 PDF per day.</p>

      <Card className="p-4 rounded-2xl border-slate-200 mb-5">
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">From</Label>
            <Input data-testid="daily-from" type="date" className="h-11 rounded-xl mt-1" value={range.from_date}
              onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input data-testid="daily-to" type="date" className="h-11 rounded-xl mt-1" value={range.to_date}
              onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} />
          </div>
          <Button onClick={load} data-testid="daily-apply-btn" className="h-11 rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]">
            <Filter className="w-4 h-4 mr-2" /> Apply
          </Button>
          <Button variant="outline" data-testid="daily-xlsx-btn" className="h-11 rounded-xl"
            onClick={() => downloadFile(`/export/daily.xlsx?from_date=${range.from_date}&to_date=${range.to_date}`, `daily_summary_${range.from_date}_to_${range.to_date}.xlsx`).catch((e) => toast.error(e.message))}>
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (period)
          </Button>
        </div>
      </Card>

      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5" data-testid="daily-period-totals">
          <Mini label="Total Sale" value={fmtINR(t.grand_sales)} blue />
          {products.map((p) => <Mini key={p.key} label={p.name} value={`${fmtNum(t[`net_${p.key}`])} ${p.unit}`} sub={fmtINR(t[`sales_${p.key}`])} />)}
          <Mini label="Paytm / UPI" value={fmtINR(t.paytm)} />
          <Mini label="Credit" value={fmtINR(t.credit)} />
          <Mini label="Card" value={fmtINR(t.card)} />
          <Mini label="Cash" value={fmtINR(t.cash)} green />
        </div>
      )}

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="daily-table">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="py-3 px-3">Date</th>
                <th className="py-3 px-3">Shifts</th>
                {products.map((p) => <React.Fragment key={p.key}><th className="py-3 px-3 text-right">{p.short} {p.unit}</th><th className="py-3 px-3 text-right">{p.short} ₹</th></React.Fragment>)}
                <th className="py-3 px-3 text-right">Testing</th>
                <th className="py-3 px-3 text-right">Total Sale</th>
                <th className="py-3 px-3 text-right">Paytm</th>
                <th className="py-3 px-3 text-right">Credit</th>
                <th className="py-3 px-3 text-right">Card</th>
                <th className="py-3 px-3 text-right">Cash</th>
                <th className="py-3 px-3 text-right">Report</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9 + products.length * 2} className="py-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && data?.rows?.length === 0 && <tr><td colSpan={9 + products.length * 2} className="py-8 text-center text-slate-500">No completed shifts in this range.</td></tr>}
              {!loading && data?.rows?.map((r) => (
                <tr key={r.business_date} data-testid={`daily-row-${r.business_date}`} className="border-t border-slate-100 hover:bg-slate-50 font-mono-num">
                  <td className="py-2.5 px-3 font-sans font-medium whitespace-nowrap">{fmtDate(r.business_date)}</td>
                  <td className="py-2.5 px-3">{r.shifts_count}</td>
                  {products.map((p) => <React.Fragment key={p.key}><td className="py-2.5 px-3 text-right">{fmtNum(r[`net_${p.key}`])}</td><td className="py-2.5 px-3 text-right">{fmtINR(r[`sales_${p.key}`])}</td></React.Fragment>)}
                  <td className="py-2.5 px-3 text-right">{fmtNum(r.total_testing_litres)}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-[color:var(--bpcl-blue)]">{fmtINR(r.grand_sales)}</td>
                  <td className="py-2.5 px-3 text-right">{fmtINR(r.paytm)}</td>
                  <td className="py-2.5 px-3 text-right">{fmtINR(r.credit)}</td>
                  <td className="py-2.5 px-3 text-right">{fmtINR(r.card)}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-emerald-700">{fmtINR(r.cash)}</td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="rounded-full h-8 mr-1" data-testid={`daily-view-${r.business_date}`}
                      onClick={() => nav(`/admin/daily/${r.business_date}`)}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full h-8" data-testid={`daily-pdf-${r.business_date}`}
                      onClick={() => pdf(r.business_date)}>
                      <Download className="w-3.5 h-3.5 mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminLayout>
  );
}

export function Mini({ label, value, sub, blue, green }) {
  return (
    <div className={`rounded-xl p-3 border ${blue ? "bg-[color:var(--bpcl-blue)]/5 border-[color:var(--bpcl-blue)]/20" : green ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><CalendarDays className="w-3 h-3 opacity-0 hidden" />{label}</div>
      <div className={`font-mono-num text-base font-bold mt-0.5 ${blue ? "text-[color:var(--bpcl-blue)]" : green ? "text-emerald-700" : ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 font-mono-num">{sub}</div>}
    </div>
  );
}
